import { useParams, Link } from 'react-router-dom';
import { usePageVersions, useRestoreVersion, usePage, useVersionDiff } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton } from '@/components/ui';
import { formatRelativeTime, formatDate } from '@/lib/utils';
import { ArrowLeft, RotateCcw, Clock, User, GitCompare, X, Check, Minus, Plus } from 'lucide-react';
import { useAuthStore } from '@/stores';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import DOMPurify from 'dompurify';
import 'highlight.js/styles/github.css';

export function PageHistory() {
  const { slug } = useParams<{ slug: string }>();
  const { data: page, isLoading: pageLoading } = usePage(slug!);
  const { data: versionsData, isLoading: versionsLoading } = usePageVersions(slug!);
  const restoreVersion = useRestoreVersion();
  const { user } = useAuthStore();
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<[number | null, number | null]>([null, null]);

  const isLoading = pageLoading || versionsLoading;
  const versions = versionsData?.items || [];

  const canEdit = user && page && (user.role === 'admin' || page.author_id === user.id);

  const handleRestore = async (versionNumber: number) => {
    if (!confirm(`确定要恢复到版本 ${versionNumber} 吗？`)) return;
    try {
      await restoreVersion.mutateAsync({ slug: slug!, version: versionNumber });
    } catch (error) {
      console.error('Failed to restore version:', error);
    }
  };

  const handleVersionSelect = (versionNumber: number) => {
    if (!compareMode) {
      setPreviewVersion(versionNumber);
      return;
    }

    // In compare mode, select two versions
    if (selectedVersions[0] === null) {
      setSelectedVersions([versionNumber, null]);
    } else if (selectedVersions[1] === null) {
      // Ensure older version is v1
      if (versionNumber < selectedVersions[0]) {
        setSelectedVersions([versionNumber, selectedVersions[0]]);
      } else {
        setSelectedVersions([selectedVersions[0], versionNumber]);
      }
    } else {
      // Reset and start over
      setSelectedVersions([versionNumber, null]);
    }
  };

  const clearCompare = () => {
    setCompareMode(false);
    setSelectedVersions([null, null]);
  };

  const previewContent = previewVersion
    ? versions.find(v => v.version_number === previewVersion)?.content
    : null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!page) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground mb-4">页面不存在</p>
          <Link to="/pages">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回列表
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to={`/pages/${slug}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{page.title} - 历史版本</h1>
            <p className="text-sm text-muted-foreground">
              共 {versionsData?.total || 0} 个版本
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!compareMode ? (
            <Button
              variant="outline"
              onClick={() => {
                setCompareMode(true);
                setPreviewVersion(null);
              }}
            >
              <GitCompare className="h-4 w-4 mr-2" />
              对比版本
            </Button>
          ) : (
            <Button variant="outline" onClick={clearCompare}>
              <X className="h-4 w-4 mr-2" />
              取消对比
            </Button>
          )}
        </div>
      </div>

      {/* Compare mode instructions */}
      {compareMode && (
        <Card className="border-primary">
          <CardContent className="py-3">
            <p className="text-sm text-muted-foreground">
              {selectedVersions[0] === null && '请选择第一个版本作为对比基准'}
              {selectedVersions[0] !== null && selectedVersions[1] === null && `已选择版本 ${selectedVersions[0]}，请选择第二个版本进行对比`}
              {selectedVersions[0] !== null && selectedVersions[1] !== null && `对比版本 ${selectedVersions[0]} 和 ${selectedVersions[1]}`}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Version list */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">版本列表</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {versions.length === 0 ? (
                <p className="text-muted-foreground text-sm">暂无历史版本</p>
              ) : (
                versions.map((version) => {
                  const isSelected = selectedVersions[0] === version.version_number || selectedVersions[1] === version.version_number;
                  const isFirst = selectedVersions[0] === version.version_number;
                  const isSecond = selectedVersions[1] === version.version_number;

                  return (
                    <div
                      key={version.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        previewVersion === version.version_number && !compareMode
                          ? 'border-primary bg-primary/5'
                          : isSelected
                          ? isFirst
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleVersionSelect(version.version_number)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {compareMode && (
                            <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                              isSelected
                                ? isFirst
                                  ? 'border-green-500 bg-green-500 text-white'
                                  : 'border-blue-500 bg-blue-500 text-white'
                                : 'border-gray-300'
                            }`}>
                              {isFirst && <span className="text-xs">A</span>}
                              {isSecond && <span className="text-xs">B</span>}
                            </div>
                          )}
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">v{version.version_number}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(version.created_at)}
                        </span>
                      </div>
                      {version.summary && (
                        <p className="text-sm text-muted-foreground mt-1 truncate">
                          {version.summary}
                        </p>
                      )}
                      <div className="flex items-center space-x-1 mt-2 text-xs text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span>{version.author?.username || 'Unknown'}</span>
                      </div>
                      {!compareMode && canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(version.version_number);
                          }}
                          disabled={restoreVersion.isPending}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          恢复此版本
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview panel */}
        <div className="lg:col-span-2">
          {compareMode && selectedVersions[0] !== null && selectedVersions[1] !== null ? (
            <VersionDiffView
              slug={slug!}
              v1={selectedVersions[0]}
              v2={selectedVersions[1]}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {previewVersion ? `版本 ${previewVersion} 预览` : compareMode ? '选择两个版本进行对比' : '选择版本预览'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {previewContent ? (
                  <div className="prose prose-slate dark:prose-invert max-w-none">
                    <div className="markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeHighlight]}
                      >
                        {DOMPurify.sanitize(previewContent)}
                      </ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    {compareMode ? '请在左侧选择两个版本进行对比' : '点击左侧版本查看内容'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// Diff view component
function VersionDiffView({ slug, v1, v2 }: { slug: string; v1: number; v2: number }) {
  const { data: diffData, isLoading, error } = useVersionDiff(slug, v1, v2);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !diffData) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">加载对比数据失败</p>
        </CardContent>
      </Card>
    );
  }

  const { v1: version1, v2: version2 } = diffData;

  // Simple line-by-line diff
  const diff = computeDiff(version1.content, version2.content);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            版本对比: v{v1} → v{v2}
          </CardTitle>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-green-200 dark:bg-green-800 rounded" />
              <span>新增</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-200 dark:bg-red-800 rounded" />
              <span>删除</span>
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-sm text-muted-foreground mt-2">
          <div>
            <span className="font-medium text-green-600">v{v1}</span>
            <span className="ml-2">{formatDate(version1.created_at)}</span>
            <span className="ml-2">by {version1.author?.username || 'Unknown'}</span>
          </div>
          <div>→</div>
          <div>
            <span className="font-medium text-blue-600">v{v2}</span>
            <span className="ml-2">{formatDate(version2.created_at)}</span>
            <span className="ml-2">by {version2.author?.username || 'Unknown'}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="font-mono text-sm divide-y">
            {diff.map((line, index) => (
              <div
                key={index}
                className={`flex ${
                  line.type === 'add'
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : line.type === 'remove'
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-transparent'
                }`}
              >
                <div className={`w-10 shrink-0 px-2 py-1 text-right text-xs border-r ${
                  line.type === 'add'
                    ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300'
                    : line.type === 'remove'
                    ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {line.type !== 'add' ? line.oldLineNum : ''}
                </div>
                <div className={`w-10 shrink-0 px-2 py-1 text-right text-xs border-r ${
                  line.type === 'add'
                    ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300'
                    : line.type === 'remove'
                    ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {line.type !== 'remove' ? line.newLineNum : ''}
                </div>
                <div className="flex-1 px-3 py-1 flex">
                  <span className={`shrink-0 mr-2 ${
                    line.type === 'add'
                      ? 'text-green-500'
                      : line.type === 'remove'
                      ? 'text-red-500'
                      : 'text-transparent'
                  }`}>
                    {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '}
                  </span>
                  <pre className="whitespace-pre-wrap break-all flex-1">
                    {line.content || ' '}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 flex gap-4 text-sm">
          <div className="flex items-center gap-2 text-green-600">
            <Plus className="h-4 w-4" />
            <span>{diff.filter(l => l.type === 'add').length} 行新增</span>
          </div>
          <div className="flex items-center gap-2 text-red-600">
            <Minus className="h-4 w-4" />
            <span>{diff.filter(l => l.type === 'remove').length} 行删除</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Check className="h-4 w-4" />
            <span>{diff.filter(l => l.type === 'same').length} 行相同</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Simple diff algorithm
interface DiffLine {
  oldLineNum: number;
  newLineNum: number;
  content: string;
  type: 'same' | 'add' | 'remove';
}

function computeDiff(oldContent: string, newContent: string): DiffLine[] {
  const oldLines = oldContent.split('\n');
  const newLines = newContent.split('\n');
  const result: DiffLine[] = [];

  // Simple LCS-based diff
  const lcs = longestCommonSubsequence(oldLines, newLines);

  let oldIdx = 0;
  let newIdx = 0;
  let lcsIdx = 0;

  while (oldIdx < oldLines.length || newIdx < newLines.length) {
    if (lcsIdx < lcs.length) {
      // Find next LCS match
      while (oldIdx < oldLines.length && oldLines[oldIdx] !== lcs[lcsIdx]) {
        result.push({
          oldLineNum: oldIdx + 1,
          newLineNum: 0,
          content: oldLines[oldIdx],
          type: 'remove',
        });
        oldIdx++;
      }
      while (newIdx < newLines.length && newLines[newIdx] !== lcs[lcsIdx]) {
        result.push({
          oldLineNum: 0,
          newLineNum: newIdx + 1,
          content: newLines[newIdx],
          type: 'add',
        });
        newIdx++;
      }
      // Add the LCS line
      if (oldIdx < oldLines.length && newIdx < newLines.length) {
        result.push({
          oldLineNum: oldIdx + 1,
          newLineNum: newIdx + 1,
          content: oldLines[oldIdx],
          type: 'same',
        });
        oldIdx++;
        newIdx++;
        lcsIdx++;
      }
    } else {
      // Add remaining lines
      while (oldIdx < oldLines.length) {
        result.push({
          oldLineNum: oldIdx + 1,
          newLineNum: 0,
          content: oldLines[oldIdx],
          type: 'remove',
        });
        oldIdx++;
      }
      while (newIdx < newLines.length) {
        result.push({
          oldLineNum: 0,
          newLineNum: newIdx + 1,
          content: newLines[newIdx],
          type: 'add',
        });
        newIdx++;
      }
    }
  }

  return result;
}

function longestCommonSubsequence(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find the LCS
  const lcs: string[] = [];
  let i = m,
    j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      lcs.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}
